import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PanelHead, Badge, Empty, Field, Btn, Advanced, SettingsCard } from "./common";
import { Plus, Trash2, Download, Database, Calendar as Cal, Bell, CalendarCheck, MessageSquare, Radio, Link2, SlidersHorizontal, Volume2, Mail, Languages, Sparkles, Wallet, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { AiVoiceSettings } from "./AiVoiceSettings";
import { SpotifyCard, WhatsAppCard, MorningBriefCard, AppleMusicCard, MusicProviderCard } from "./IntegrationCards";
import { BillingProfileCard } from "./FinancePanel";
import { useSpeech } from "@/components/SpeechPlayer";
import { GmailConnectionCard } from "./GmailConnectionCard";
import { CalendarConnectionCard } from "./CalendarConnectionCard";
import { MailAccountsCard } from "./MailAccountsCard";
import { CalendarPanelReal } from "./CalendarPanelReal";
import { MicSettingsCard, WakeWordToggleCard } from "./MicSettingsCard";

// ---------------- MEMORY ----------------
export const MemoryPanel = () => {
  const { t, tx } = useI18n();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = async () => { const r = await api.get("/memory"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.key.trim()) return toast.error(tx("Anahtar gerekli"));
    if (form.fact_id) await api.put(`/memory/${form.fact_id}`, form); else await api.post("/memory", { ...form, source: "user", confidence: "verified" });
    setForm(null); load();
  };
  const del = async (id) => { await api.delete(`/memory/${id}`); load(); };
  const exportData = async () => {
    const r = await api.get("/memory/export");
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "jarvis-backup.json"; a.click();
    toast.success(tx("Yedek indirildi"));
  };
  const byCat = items.reduce((a, x) => { (a[x.category] = a[x.category] || []).push(x); return a; }, {});
  return (
    <div data-testid="panel-memory">
      <PanelHead title={t("nav_memory")} sub={tx("Kalıcı iş hafızası — doğrulanmış vs tahmin ayrımı")}
        right={<div className="flex gap-2"><Btn variant="ghost" onClick={exportData} data-testid="export-memory-btn"><Download size={14} className="inline mr-1" />{tx("Dışa aktar")}</Btn><Btn onClick={() => setForm({ category: "general", key: "", value: "" })} data-testid="add-memory-btn"><Plus size={14} className="inline mr-1" />{t("add")}</Btn></div>} />
      {form && (
        <div className="panel p-4 mb-5">
          <div className="grid md:grid-cols-3 gap-3">
            <Field label={tx("Kategori")} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <Field label={tx("Anahtar")} value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} data-testid="memory-key-input" />
            <Field label={tx("Değer")} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="memory-value-input" />
          </div>
          <div className="flex gap-2"><Btn onClick={save} data-testid="save-memory-btn">{t("save")}</Btn><Btn variant="ghost" onClick={() => setForm(null)}>{t("cancel")}</Btn></div>
        </div>
      )}
      {items.length === 0 && <Empty text={t("noData")} />}
      {Object.entries(byCat).map(([cat, list]) => (
        <div key={cat} className="mb-5">
          <div className="label-eyebrow mb-2">{cat}</div>
          <div className="space-y-2">
            {list.map((x) => (
              <div key={x.fact_id} className="panel p-3 flex items-center justify-between" data-testid="memory-row">
                <div><span className="text-white text-sm">{x.key}:</span> <span className="text-cyan-200 text-sm">{x.value}</span></div>
                <div className="flex items-center gap-2">
                  <Badge color={x.confidence === "verified" ? "green" : "amber"}>{x.source === "verified" ? tx("doğrulanmış") : x.source}</Badge>
                  <button onClick={() => setForm(x)} className="text-white/50 hover:text-cyan-300 text-xs">{t("edit")}</button>
                  <button onClick={() => del(x.fact_id)} className="text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------- USAGE ----------------
export const UsagePanel = () => {
  const { t, tx } = useI18n();
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    api.get("/usage/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/ai/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="panel-usage-cost">
      <PanelHead title={t("nav_usage")} sub={tx("Gerçek API kullanımına dayalı maliyet (USD)")} />
      <div className="panel corner-bracket p-6 mb-5 text-center">
        <div className="label-eyebrow">{tx("Toplam (bu hesap)")}</div>
        <div className="text-4xl font-mono text-cyan-200 mt-2">${data?.total_cost_usd?.toFixed(4) ?? "0.0000"}</div>
        {data?.any_estimated && <div className="text-xs text-amber-300 mt-2">{tx("Bazı kalemler tahmini / veri yok")}</div>}
        <p className="text-xs text-white/50 mt-3 max-w-lg mx-auto">{data?.note}</p>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        {data && Object.keys(data.by_model).length === 0 && <Empty text={tx("Henüz model kullanımı yok (AI bağlanınca dolacak)")} />}
        {data && Object.entries(data.by_model).map(([m, v]) => (
          <div key={m} className="panel p-4" data-testid="usage-model-row">
            <div className="text-white font-medium">{m}</div>
            <div className="mt-2 space-y-1 text-xs font-mono text-white/70">
              <div className="flex justify-between"><span>{tx("çağrı")}</span><span>{v.calls}</span></div>
              <div className="flex justify-between"><span>{tx("giriş tok.")}</span><span>{v.input_tokens}</span></div>
              <div className="flex justify-between"><span>{tx("çıkış tok.")}</span><span>{v.output_tokens}</span></div>
              <div className="flex justify-between text-cyan-200"><span>{tx("maliyet")}</span><span>${v.cost_usd.toFixed(5)}</span></div>
            </div>
          </div>
        ))}
      </div>
      {pricing && (
        <div className="panel p-4">
          <div className="label-eyebrow mb-3">{tx("Fiyat yapılandırması · kaynak: güncel {d}", { d: pricing.updated_at })}</div>
          <div className="space-y-2 text-sm">
            {Object.entries(pricing.models).map(([id, m]) => (
              <div key={id} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2">
                <span className="text-white/90">{m.label}</span>
                <span className="font-mono text-xs text-cyan-200">
                  {m.per_second !== undefined ? (m.per_second == null ? tx("saniye: veri yok") : `$${m.per_second}/sn`)
                    : `in $${m.input_per_1m} / out $${m.output_per_1m} (1M)`}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-white/40 mt-3">{tx("Emergent geliştirme/barındırma kredileri ile AI kullanım maliyeti ayrı kalemlerdir. Platformun gerçek bakiye API'si olmadan tahmini bakiye gösterilmez.")}</p>
        </div>
      )}
    </div>
  );
};

// ---------------- NOTIFICATIONS + AUTOMATIONS ----------------
export const NotificationsPanel = () => {
  const { t, tx } = useI18n();
  const [items, setItems] = useState([]);
  const [autos, setAutos] = useState([]);
  const loadNotifs = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  const loadAutos = () => api.get("/automations").then((r) => setAutos(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); loadAutos(); }, []);
  const toggle = async (a) => {
    const next = a.status === "paused" || a.status === "error" ? "active" : "paused";
    try { await api.put(`/automations/${a.automation_id}/status`, { status: next }); loadAutos(); }
    catch { toast.error(tx("İşlem başarısız")); }
  };
  const removeAuto = async (a) => {
    if (!window.confirm(tx("Bu otomasyon kaldırılsın mı?"))) return;
    try { await api.delete(`/automations/${a.automation_id}`); loadAutos(); toast.success(tx("Otomasyon kaldırıldı")); }
    catch { toast.error(tx("Kaldırılamadı")); }
  };
  const [brief, setBrief] = useState("");
  const speech = useSpeech({ setOrbState: () => {}, fallbackState: "idle" });
  // The brief was prepared server-side (works while the browser was closed); audio plays ONLY here, on request.
  const listenBrief = async () => {
    try {
      const r = await api.get("/briefs/latest");
      if (!r.data?.text) { toast.info(tx("Henüz sabah özeti yok.")); return; }
      setBrief(r.data.text);
      try { const url = await speech.fetchSpeech(r.data.text); await speech.playUrl("brief", url, { wait: false }); }
      catch { toast.info(tx("Sesli okuma kullanılamıyor; metin gösteriliyor.")); }
    } catch { toast.error(tx("Özet alınamadı")); }
  };
  const statusColor = (s) => (s === "active" ? "green" : s === "error" ? "red" : "amber");
  const statusText = (s) => (s === "active" ? tx("aktif") : s === "error" ? tx("hata") : tx("durduruldu"));
  return (
    <div data-testid="panel-notifications">
      <PanelHead title={t("nav_notifications")} sub={tx("Arka plan e-posta takibi ve uygulama içi bildirimler")} />

      <div className="label-eyebrow mb-2">{tx("Otomasyonlar")}</div>
      {autos.length === 0 ? (
        <div className="panel p-3 mb-5 text-xs text-white/60">
          {tx("Henüz otomasyon yok. Jarvis'e \"her 30 dakikada yeni maillerimi kontrol et, önemlileri haber ver\" diyebilirsin. Bildirimler yalnızca uygulama içidir.")}
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          {autos.map((a) => (
            <div key={a.automation_id} className="panel p-3" data-testid="automation-row">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{a.title}</div>
                  <div className="text-xs text-white/50">{a.schedule_label}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge color={statusColor(a.status)}>{statusText(a.status)}</Badge>
                  <button onClick={() => toggle(a)} data-testid="automation-toggle-btn"
                    className="text-xs text-cyan-300 hover:text-cyan-200 border border-cyan-400/30 rounded-md px-2 py-1">
                    {a.status === "active" ? tx("Durdur") : tx("Devam")}
                  </button>
                  <button onClick={() => removeAuto(a)} data-testid="automation-delete-btn"
                    className="text-xs text-red-300/80 hover:text-red-300 border border-red-400/20 rounded-md px-2 py-1">
                    {tx("Sil")}
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs text-white/50 flex flex-wrap gap-x-4 gap-y-1">
                {a.last_result && <span>{tx("Son:")} {a.last_result}</span>}
                {a.last_error && <span className="text-red-300">{tx("Hata:")} {a.last_error}</span>}
                {a.status === "active" && a.next_run && <span>{tx("Sonraki:")} {new Date(a.next_run).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="label-eyebrow mb-2">{tx("Bildirimler")}</div>
      {brief && <div className="panel p-3 mb-3 text-sm text-cyan-50" data-testid="brief-text">{brief}</div>}
      {items.length === 0 ? <Empty text={t("noData")} /> : items.map((x) => (
        <div key={x.notif_id} className={`panel p-3 mb-2 flex items-center gap-3 ${x.read ? "opacity-60" : ""}`} data-testid="notification-row">
          <Bell size={15} className="text-cyan-300 shrink-0" /><span className="text-sm text-white flex-1">{tx(x.message)}</span>
          {x.kind === "morning_brief" && (
            <Btn variant="ghost" onClick={listenBrief} data-testid="brief-listen">{tx("Dinle")}</Btn>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------- CALENDAR (real Google Calendar) ----------------
export const CalendarPanel = CalendarPanelReal;

// ---------------- TTS settings (voice + auto-read) ----------------
const TtsSettings = () => {
  const { tx } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/tts/status").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-xs text-white/40">{tx("Durum alınıyor…")}</div>;
  const save = async (patch) => { await api.put("/settings", patch); setS({ ...s, ...(patch.tts_voice ? { voice: patch.tts_voice } : {}), ...(patch.tts_auto_read !== undefined ? { auto_read: patch.tts_auto_read } : {}) }); toast.success(tx("Kaydedildi")); };
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Badge color={s.available ? "green" : "amber"} testid="tts-status-badge">{s.available ? `${tx("Hazır")} · ${s.model}` : tx("Kullanılamıyor")}</Badge>
        <Badge color="cyan">{tx("Emergent kredisi")} · ~${s.per_1m_chars_usd}/1M {tx("karakter")}</Badge>
        <Badge color={s.auto_read ? "cyan" : "gray"}>{tx("otomatik okuma")} {s.auto_read ? tx("açık") : tx("kapalı")}</Badge>
        <Btn variant="ghost" onClick={() => save({ tts_auto_read: !s.auto_read })} data-testid="tts-auto-toggle-settings">{s.auto_read ? tx("Otomatik okumayı kapat") : tx("Otomatik okumayı aç")}</Btn>
      </div>
      <div className="label-eyebrow mb-2">{tx("Ses")}</div>
      <div className="flex gap-2 flex-wrap">
        {s.voices.map((v) => (
          <button key={v} onClick={() => save({ tts_voice: v })} data-testid={`tts-voice-${v}`}
            className={`px-3 py-1.5 rounded-lg border text-xs font-mono ${s.voice === v ? "bg-cyan-500/25 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/15 text-white/70"}`}>{v}</button>
        ))}
      </div>
      <p className="text-xs text-white/45 mt-3">{s.language_note} {s.message || ""} {tx("Bu akış GPT-Live değildir; insan Türkçe telaffuz değerlendirmesi henüz yapılmadı.")}</p>
    </div>
  );
};

// ---------------- Benim işim (business profile via company-settings) ----------------
const BusinessProfileCard = () => {
  const { tx } = useI18n();
  const [bp, setBp] = useState(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { api.get("/company-settings").then((r) => setBp(r.data?.business_profile || {})).catch(() => {}); }, []);
  if (!bp) return <div className="text-xs text-white/40">{tx("Yükleniyor…")}</div>;
  const set = (k, v) => { setBp({ ...bp, [k]: v }); setDirty(true); };
  const save = async () => {
    try { await api.put("/company-settings", { business_profile: bp }); setDirty(false); toast.success(tx("Kaydedildi")); }
    catch { toast.error(tx("Kaydedilemedi")); }
  };
  const Pick = ({ k, options }) => (
    <div className="flex gap-2">
      {options.map(([val, lbl]) => (
        <button key={val} onClick={() => set(k, val)} data-testid={`bp-${k}-${val}`}
          className={`px-3 py-1.5 rounded-lg border text-xs ${bp[k] === val ? "bg-cyan-500/25 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/15 text-white/70"}`}>{lbl}</button>
      ))}
    </div>
  );
  return (
    <div data-testid="business-profile">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={tx("Şirket adı")} value={bp.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="bp-company-input" />
        <Field label={tx("VAT numarası")} value={bp.vat_number || ""} onChange={(e) => set("vat_number", e.target.value)} data-testid="bp-vat-input" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-3">
        <div><div className="label-eyebrow mb-1.5">{tx("Varsayılan dil")}</div><Pick k="default_language" options={[["tr", "Türkçe"], ["en", "English"]]} /></div>
        <div><div className="label-eyebrow mb-1.5">{tx("Asistan üslubu")}</div><Pick k="assistant_tone" options={[["short", tx("Kısa")], ["detailed", tx("Detaylı")]]} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <Field label={tx("Hitap (opsiyonel)")} value={bp.address_term || ""} onChange={(e) => set("address_term", e.target.value)} data-testid="bp-address-input" />
        <Field label={tx("Sessiz saat (bilgi, uygulama içi)")} value={bp.quiet_hours || ""} onChange={(e) => set("quiet_hours", e.target.value)} placeholder="22:00-08:00" />
      </div>
      <label className="block mt-3"><span className="label-eyebrow block mb-1">{tx("Teklif tercihleri / şartlar")}</span>
        <textarea value={bp.quote_prefs || ""} onChange={(e) => set("quote_prefs", e.target.value)} rows={2} data-testid="bp-quote-input"
          className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60" /></label>
      <label className="block mt-3"><span className="label-eyebrow block mb-1">{tx("İmza tercihi")}</span>
        <textarea value={bp.signature_pref || ""} onChange={(e) => set("signature_pref", e.target.value)} rows={2} data-testid="bp-signature-input"
          className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60" /></label>
      <label className="block mt-3"><span className="label-eyebrow block mb-1">{tx("Önemli e-posta ölçütü (otomasyon sınıflandırması için)")}</span>
        <textarea value={bp.important_mail_criteria || ""} onChange={(e) => set("important_mail_criteria", e.target.value)} rows={2} data-testid="bp-mailcrit-input"
          className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60" /></label>
      <div className="mt-3"><Btn onClick={save} disabled={!dirty} data-testid="bp-save-btn">{dirty ? tx("Kaydet") : tx("Kaydedildi")}</Btn></div>
      <p className="text-[0.7rem] text-white/40 mt-2">{tx("Bu tercihler Hafıza ile birlikte asistanın taslak ve yanıtlarını kişiselleştirmek için kullanılır. Eksik bilgi uydurulmaz.")}</p>
    </div>
  );
};

// ---------------- SETTINGS ----------------
// Real per-path status (no network): research follows the SELECTED text mode in chat, and the own-OpenAI
// key in Live voice. News is the free trusted-RSS path. Music has no integration (paused).
const PendingFeedsCard = ({ research }) => {
  const { tx } = useI18n();
  const textOk = !!research?.text?.available;
  const liveOk = !!research?.live?.available;
  const researchNeed = !research ? tx("Durum alınıyor…")
    : [textOk ? tx("Yazışma: kendi OpenAI anahtarı modunda web araması hazır.")
         : research.text?.reason === "unsupported_in_mode"
           ? tx("Yazışma: platform (Emergent) modunda web araması yok; Sohbet sekmesinden kendi OpenAI anahtarı modunu seçin.")
           : tx("Yazışma: sunucuda OPENAI_API_KEY yok."),
       liveOk ? tx("Live konuşma: kendi OpenAI anahtarınızla hazır.") : tx("Live konuşma: sunucuda OPENAI_API_KEY yok."),
       tx("Arama yalnızca siz açıkça araştırma istediğinizde yapılır (ücretli).")].join(" ");
  return (
  <div data-testid="pending-feeds-card" className="space-y-2.5">
    <p className="text-[0.72rem] text-white/55">
      {tx("Asistan sahte haber/sonuç üretmez veya \"çalıyor\" göstermez; bağlı olmayan kaynak kartta açıkça belirtilir.")}
    </p>
    {[
      ["research", tx("Araştırma (web arama)"), researchNeed, (textOk || liveOk) ? tx("hazır") : tx("bağlantı bekliyor"), textOk || liveOk],
      ["news", tx("Haberler"), tx("Ücretsiz güvenilir yayıncı RSS akışları (Dünya / İngiltere / Türkiye). Konu bazlı haber için araştırma kullanılır."), tx("hazır"), true],
    ].map(([id, title, need, status, ok]) => (
      <div key={id} className={`bg-black/20 border ${ok ? "border-emerald-400/15" : "border-amber-400/15"} rounded-lg px-3 py-2.5`} data-testid={`feed-${id}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-sm text-white/85">{title}</span>
          <Badge color={ok ? "green" : "amber"}>{status}</Badge>
        </div>
        <p className="text-[0.7rem] text-amber-200/80 mt-1">{need}</p>
      </div>
    ))}
    <p className="text-[0.68rem] text-white/40">{tx("Ücretli abonelik veya yeni hesap erişimi otomatik açılmaz; sağlayıcı ve kimlik bilgilerini siz onaylarsınız.")}</p>
  </div>
  );
};

export const SettingsPanel = ({ aiStatus, onRecheck, reduceMotion, setReduceMotion, onVoiceModeChange }) => {
  const { t, tx, lang, setLang } = useI18n();
  const [settings, setSettings] = useState({});
  const [tab, setTab] = useState("chat");
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);
  const save = async (patch) => { const next = { ...settings, ...patch }; setSettings(next); await api.put("/settings", patch); };

  const TABS = [
    ["chat", t("tab_chat"), MessageSquare],
    ["voice", t("tab_voice"), Radio],
    ["business", t("tab_business"), Briefcase],
    ["links", t("tab_links"), Link2],
    ["general", t("tab_general"), SlidersHorizontal],
  ];

  return (
    <div data-testid="panel-settings">
      <PanelHead title={t("nav_settings")} sub={t("set_sub")} />

      <div className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-thin -mx-1 px-1" role="tablist" data-testid="settings-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`settings-tab-${id}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-sm transition-colors ${tab === id ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/10 text-white/60 hover:text-cyan-200 hover:border-cyan-400/30"}`}>
            <Icon size={13} />{label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        {tab === "chat" && (
          <SettingsCard title={t("card_chat")} icon={MessageSquare} testid="settings-chat-card" wide>
            <AiVoiceSettings onRecheck={onRecheck} onVoiceModeChange={onVoiceModeChange} />
          </SettingsCard>
        )}

        {tab === "voice" && (
          <>
            <SettingsCard title={t("card_wakeword")} icon={Radio} testid="settings-wakeword-card">
              <WakeWordToggleCard />
            </SettingsCard>
            <SettingsCard title={t("card_mic")} icon={Radio} testid="settings-mic-card">
              <MicSettingsCard />
            </SettingsCard>
            <SettingsCard title={t("card_tts")} icon={Volume2} testid="settings-tts-card">
              <p className="text-[0.72rem] text-white/50 mb-2">{t("tts_note")}</p>
              <Advanced label={t("voice_pick")} testid="tts-advanced"><TtsSettings /></Advanced>
            </SettingsCard>
          </>
        )}

        {tab === "business" && (
          <>
            <SettingsCard title={t("card_business")} icon={Briefcase} testid="settings-business-card" wide>
              <BusinessProfileCard />
            </SettingsCard>
            <SettingsCard title={tx("Fatura bilgileri · yasal ad, adres, VAT, banka")} icon={Wallet} testid="settings-billing-card" wide>
              <BillingProfileCard />
            </SettingsCard>
          </>
        )}

        {tab === "links" && (
          <>
            <SettingsCard title={t("card_gmail")} icon={Mail} testid="settings-gmail-card">
              <GmailConnectionCard />
            </SettingsCard>
            <SettingsCard title={t("card_calendar")} icon={CalendarCheck} testid="settings-calendar-card">
              <CalendarConnectionCard />
            </SettingsCard>
            <SettingsCard title={t("card_mail_accounts")} icon={Mail} testid="settings-mail-accounts-card" wide>
              <MailAccountsCard />
            </SettingsCard>
            <SettingsCard title={tx("Müzik sağlayıcısı")} icon={Radio} testid="settings-music-provider-card">
              <MusicProviderCard />
            </SettingsCard>
            <SettingsCard title={tx("Spotify")} icon={Radio} testid="settings-spotify-card">
              <SpotifyCard />
            </SettingsCard>
            <SettingsCard title={tx("Apple Music")} icon={Radio} testid="settings-apple-music-card">
              <AppleMusicCard />
            </SettingsCard>
            <SettingsCard title={tx("WhatsApp")} icon={MessageSquare} testid="settings-whatsapp-card">
              <WhatsAppCard />
            </SettingsCard>
            <SettingsCard title={t("card_feeds")} icon={Radio} testid="settings-feeds-card" wide>
              <PendingFeedsCard research={aiStatus?.research} />
            </SettingsCard>
          </>
        )}

        {tab === "general" && (
          <>
            <SettingsCard title={t("card_language")} icon={Languages} testid="settings-language-card">
              <div className="flex gap-2">
                {["tr", "en"].map((l) => (
                  <button key={l} onClick={() => { setLang(l); save({ language: l }); }} data-testid={`lang-${l}-btn`}
                    className={`px-4 py-2 rounded-lg border text-sm ${lang === l ? "bg-cyan-500/25 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/15 text-white/70"}`}>{l.toUpperCase()}</button>
                ))}
              </div>
            </SettingsCard>

            <SettingsCard title={tx("Sabah özeti")} icon={Bell} testid="settings-brief-card">
              <MorningBriefCard />
            </SettingsCard>

            <SettingsCard title={t("card_motion")} icon={Sparkles} testid="settings-motion-card"
              right={<Badge color={reduceMotion ? "amber" : "cyan"}>{reduceMotion ? t("motion_reduced") : t("motion_smooth")}</Badge>}>
              <Btn variant="ghost" onClick={() => setReduceMotion?.(!reduceMotion)} data-testid="reduce-motion-toggle">
                {reduceMotion ? t("motion_enable") : t("motion_reduce")}
              </Btn>
              <p className="text-[0.7rem] text-white/45 mt-2">{tx("Sistemin \"hareketi azalt\" tercihine her zaman uyulur.")}</p>
            </SettingsCard>

            <SettingsCard title={tx("Harcama sınırı (USD)")} icon={Wallet} testid="settings-spend-card" wide>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={tx("Günlük")} type="number" value={settings.daily_spend_limit_usd || ""}
                  onChange={(e) => save({ daily_spend_limit_usd: e.target.value ? +e.target.value : null })} />
                <Field label={tx("Aylık")} type="number" value={settings.monthly_spend_limit_usd || ""}
                  onChange={(e) => save({ monthly_spend_limit_usd: e.target.value ? +e.target.value : null })} />
              </div>
              <p className="text-[0.7rem] text-white/45">{tx("Boş = sınırsız. Bu sınır AI kullanım maliyeti içindir (OpenAI kendi anahtarınızda USD, Emergent modunda kredi ayrı sayılır). Devam eden istekler ve geciken kullanım kayıtları sınırı küçük miktarda aşabilir.")}</p>
            </SettingsCard>
          </>
        )}
      </div>
    </div>
  );
};
